import { useEffect, useState, useCallback } from "react";

/**
 * LGPD-compliant consent state.
 * Three categories: necessary (always on), analytics, marketing.
 *
 * State persisted in localStorage. Fires window events so non-React code
 * (index.html script tags) can react to changes.
 */

const STORAGE_KEY = "iag_consent_v1";
const EVENT_UPDATED = "iag:consent:updated";

const DEFAULT_STATE = {
  decided: false,
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const readState = () => {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return DEFAULT_STATE;
  }
};

const writeState = (state) => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(EVENT_UPDATED, { detail: state }));
};

/**
 * InterAct Tracking Foundation milestone (2026-10-09, pass 3) — a
 * plain (non-hook) reader of this SAME localStorage state, for the
 * handful of callers that are not React components (e.g.
 * `pages/NextPresentationReadinessCheck/api.js`, a plain fetch
 * wrapper) but still need to send the visitor's CURRENT
 * analytics-consent decision up to the backend at diagnostic-
 * completion / contact-submission time, so it can be recorded there
 * as the EXPLICIT consent state `posthog_tracking_dispatch.py` checks
 * before projecting to PostHog.
 *
 * Returns "granted"/"not_granted" ONLY once the banner has actually
 * been interacted with (`decided === true`) — returns `null` when the
 * visitor has not yet decided, so the backend never receives a
 * fabricated/guessed state for an undecided visitor.
 */
export const getAnalyticsConsentState = () => {
  const state = readState();
  if (!state.decided) return null;
  return state.analytics ? "granted" : "not_granted";
};


export const useConsent = () => {
  const [state, setState] = useState(DEFAULT_STATE);

  useEffect(() => {
    setState(readState());
    const onUpdate = (e) => setState(e.detail);
    window.addEventListener(EVENT_UPDATED, onUpdate);
    return () => window.removeEventListener(EVENT_UPDATED, onUpdate);
  }, []);

  const acceptAll = useCallback(() => {
    const next = { decided: true, necessary: true, analytics: true, marketing: true, timestamp: Date.now() };
    writeState(next);
  }, []);

  const rejectAll = useCallback(() => {
    const next = { decided: true, necessary: true, analytics: false, marketing: false, timestamp: Date.now() };
    writeState(next);
  }, []);

  const savePartial = useCallback((partial) => {
    const next = { ...readState(), ...partial, decided: true, necessary: true, timestamp: Date.now() };
    writeState(next);
  }, []);

  const reset = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    writeState(DEFAULT_STATE);
  }, []);

  return { ...state, acceptAll, rejectAll, savePartial, reset };
};
